import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ServiceTaskTypeEnum } from "@/domain/models/common";
import { TasksType } from "@/domain/models/job";
import {
  AssignTaskToDriverParamsType,
  JobServiceTaskBaseType,
  StartTaskFunctionRequestType,
  StartTaskFunctionResultType,
  UpdateConnectedTaskType,
  UpdateTaskCleanupParamsType,
  UpdateTaskDueAtParamsType,
} from "@/src/domain/task";

import { TaskApi } from "../api/TaskApi";
import { JobQueryKeys } from "../queries/jobQueries";
import { ServiceQueryKeys } from "../queries/serviceQueries";
import { TaskQueryKeys, useTasksToDumpQuery } from "../queries/taskQueries";

export const useAssignTaskToDriverMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AssignTaskToDriverParamsType,
    unknown,
    AssignTaskToDriverParamsType
  >({
    mutationFn: params => TaskApi.assignTaskToDriver(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    },
  });

  return mutation;
};

export const useUnassignTaskFromDriverMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    JobServiceTaskBaseType,
    unknown,
    JobServiceTaskBaseType
  >({
    mutationFn: params => TaskApi.unassignTaskFromDriver(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    },
  });

  return mutation;
};

export const useUpdateTaskDueAtMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    UpdateTaskDueAtParamsType,
    unknown,
    UpdateTaskDueAtParamsType
  >({
    mutationFn: params => TaskApi.updateTaskDueAt(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    },
  });

  return mutation;
};

export const useStartTaskMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    StartTaskFunctionResultType,
    unknown,
    StartTaskFunctionRequestType
  >({
    mutationFn: params => TaskApi.startTask(params),
    onSuccess: startedTask => {
      const { jobId, serviceId, taskId } = startedTask;

      queryClient.setQueryData(
        TaskQueryKeys.details({ jobId, serviceId, taskId }),
        (oldData: TasksType | undefined) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            ...startedTask,
          };
        },
      );

      if (
        taskId === ServiceTaskTypeEnum.JUNKREMOVAL ||
        taskId === ServiceTaskTypeEnum.PICKUPBIN
      ) {
        queryClient.setQueryData(
          ServiceQueryKeys.details({ jobId, serviceId }),
          (oldData: TasksType | undefined) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              ...startedTask,
            };
          },
        );
      }
    },
  });

  return mutation;
};

export const useUpdateCleanupTypeMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    UpdateTaskCleanupParamsType,
    unknown,
    UpdateTaskCleanupParamsType
  >({
    mutationFn: params => TaskApi.updateTaskCleanupType(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: JobQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    },
  });

  return mutation;
};

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<Partial<TasksType>, unknown, Partial<TasksType>>(
    {
      mutationFn: params => TaskApi.updateTask(params),
      onSuccess: updatedTask => {
        const { jobId, serviceId, taskId } = updatedTask;

        if (!jobId || !serviceId || !taskId) {
          return;
        }

        queryClient.setQueryData(
          TaskQueryKeys.details({ jobId, serviceId, taskId }),
          (oldData: TasksType | undefined) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              ...updatedTask,
            };
          },
        );
      },
    },
  );

  return mutation;
};

export const useUpdateConnectedTaskMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    UpdateConnectedTaskType,
    unknown,
    UpdateConnectedTaskType
  >({
    mutationFn: params => TaskApi.updateConnectedTask(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ServiceQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    },
  });

  return mutation;
};

export const useCompleteTaskMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    JobServiceTaskBaseType,
    unknown,
    JobServiceTaskBaseType
  >({
    mutationFn: params => TaskApi.completeTask(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: ServiceQueryKeys.all });
    },
  });

  return mutation;
};

export const useUpdateAllDriverDumpTasksMutation = () => {
  const queryClient = useQueryClient();

  const { data: tasksToDump } = useTasksToDumpQuery();

  const tasksToDumpRefs = tasksToDump?.map(task => TaskApi.getRef(task));

  const mutation = useMutation({
    mutationFn: (taskParams: Partial<TasksType>) =>
      TaskApi.updateTasksInTransaction({
        tasksToUpdate: tasksToDumpRefs ?? [],
        taskParams,
      }),
    onSuccess: result => {
      const { updatedTasks, taskParams } = result;

      const promises = updatedTasks.map(async updatedTask => {
        const updatedTaskData = await TaskApi.getTaskByRef(updatedTask);

        if (!updatedTaskData) {
          return;
        }

        const { jobId, serviceId, taskId } = updatedTaskData;

        queryClient.setQueryData(
          TaskQueryKeys.details({ jobId, serviceId, taskId }),
          (oldData: TasksType | undefined) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              ...taskParams,
            };
          },
        );
      });

      return Promise.allSettled(promises);
    },
  });

  return mutation;
};
